import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Download,
  FileSearch,
  History,
  Loader2,
  Play,
  RefreshCw,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { DocumentStatusBadge, RunStatusBadge, ValueStatusBadge } from '../components/Reports/ReportStatus';
import CompletionWorkbench from '../components/Reports/CompletionWorkbench';
import type { ReportsOutletContext } from '../components/Reports/ReportsLayout';
import apiService from '../services/api';
import type { CompanyReportDetail, CorpusRunSummary, CorpusValue, CorpusValueStatus } from '../types';
import {
  analysisProfileLabel,
  completionPercent,
  corpusTotalCount,
  downloadCorpusCsv,
  formatReportDate,
  reportPeriodLabel,
} from '../utils/reportDashboard';
import { buildReportChatSuggestions } from '../utils/reportChat';

type StatusFilter = 'all' | CorpusValueStatus;
type SortKey = 'item_key' | 'name' | 'status';
type SortDirection = 'asc' | 'desc';

const statusOrder: Record<CorpusValueStatus, number> = {
  flagged: 0,
  needs_review: 1,
  missing: 2,
  filled: 3,
  calculated: 3,
  not_applicable: 4,
};

const runLabel = (run: CorpusRunSummary): string => {
  const date = run.created_at
    ? new Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(run.created_at))
    : 'ריצה חדשה';
  return `${date} · ${run.spec_version_label}`;
};

const ReportsReportPage: React.FC = () => {
  const { reportId = '' } = useParams<{ reportId: string }>();
  const { setReportChatContext } = useOutletContext<ReportsOutletContext>();
  const queryClient = useQueryClient();
  const [selectedRunId, setSelectedRunId] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('item_key');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [runDepth, setRunDepth] = useState<'easy' | 'all'>('all');

  const reportQuery = useQuery({
    queryKey: ['company-report', reportId],
    queryFn: () => apiService.getCompanyReport(reportId),
    enabled: !!reportId,
    refetchInterval: (query) => {
      const data = query.state.data;
      const active = data?.runs.some((run) => run.status === 'pending' || run.status === 'running');
      const processing = data?.report.document_status === 'processing' || data?.report.document_status === 'uploaded';
      return active || processing ? 4000 : false;
    },
  });

  useEffect(() => {
    if (!selectedRunId && reportQuery.data?.runs[0]?.run_id) {
      setSelectedRunId(reportQuery.data.runs[0].run_id);
    }
  }, [reportQuery.data?.runs, selectedRunId]);

  const reportData = reportQuery.data as CompanyReportDetail | undefined;
  const selectedRunMeta = reportData?.runs.find((run) => run.run_id === selectedRunId);
  const runQuery = useQuery({
    queryKey: ['corpus-run', selectedRunId],
    queryFn: () => apiService.getCorpusRun(selectedRunId),
    enabled: !!selectedRunId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? 2500 : false;
    },
  });

  const runStatus = runQuery.data?.status || selectedRunMeta?.status;
  const runStats = runQuery.data?.stats || selectedRunMeta?.stats;
  const valuesQuery = useQuery({
    queryKey: ['corpus-values', selectedRunId],
    queryFn: () => apiService.getCorpusValues(selectedRunId),
    enabled: !!selectedRunId && runStatus === 'done',
  });

  useEffect(() => {
    if (runStatus === 'done') {
      void queryClient.invalidateQueries({ queryKey: ['company-report', reportId] });
    }
  }, [runStatus, queryClient, reportId]);

  const startRun = useMutation({
    mutationFn: () => apiService.startCorpusRun(reportId, {
      difficulty: runDepth === 'all' ? null : 'easy',
    }),
    onSuccess: (run) => {
      setSelectedRunId(run.run_id);
      setSelectedCategory('all');
      setStatusFilter('all');
      toast.success('ריצת החילוץ החלה');
      void queryClient.invalidateQueries({ queryKey: ['company-report', reportId] });
    },
    onError: (error: unknown) => {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || 'לא הצלחנו להתחיל את ריצת החילוץ');
    },
  });

  const values = useMemo<CorpusValue[]>(() => valuesQuery.data || [], [valuesQuery.data]);
  const reportChatContext = useMemo(() => {
    const currentReport = reportData?.report;
    if (!currentReport) return null;
    return {
      reportId: currentReport.id,
      runId: runStatus === 'done' ? selectedRunId : undefined,
      documentId: currentReport.document_id,
      documentName: currentReport.document_name,
      companyName: currentReport.company_name,
      periodLabel: reportPeriodLabel(currentReport),
      suggestions: buildReportChatSuggestions(values),
    };
  }, [reportData?.report, runStatus, selectedRunId, values]);

  useEffect(() => {
    setReportChatContext(reportChatContext);
  }, [reportChatContext, setReportChatContext]);

  useEffect(() => () => {
    setReportChatContext((current) => current?.reportId === reportId ? null : current);
  }, [reportId, setReportChatContext]);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const value of values) counts.set(value.category, (counts.get(value.category) || 0) + 1);
    return Array.from(counts.entries()).sort(([a], [b]) => a.localeCompare(b, 'he'));
  }, [values]);

  const statusCounts = useMemo(() => {
    const counts: Record<StatusFilter, number> = {
      all: values.length,
      filled: 0,
      calculated: 0,
      needs_review: 0,
      missing: 0,
      not_applicable: 0,
      flagged: 0,
    };
    values.forEach((value) => { counts[value.status] += 1; });
    return counts;
  }, [values]);

  const filteredValues = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('he');
    const filtered = values.filter((value) => {
      if (selectedCategory !== 'all' && value.category !== selectedCategory) return false;
      if (statusFilter !== 'all' && value.status !== statusFilter) return false;
      if (!needle) return true;
      return [value.item_key, value.name, value.value, value.category, value.section_title]
        .some((part) => part?.toLocaleLowerCase('he').includes(needle));
    });
    return filtered.sort((a, b) => {
      let result = 0;
      if (sortKey === 'status') result = statusOrder[a.status] - statusOrder[b.status];
      else result = String(a[sortKey] || '').localeCompare(String(b[sortKey] || ''), 'he', { numeric: true });
      return sortDirection === 'asc' ? result : -result;
    });
  }, [values, selectedCategory, statusFilter, search, sortKey, sortDirection]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDirection((direction) => direction === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const toggleExpanded = (itemKey: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(itemKey)) next.delete(itemKey);
      else next.add(itemKey);
      return next;
    });
  };

  if (reportQuery.isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" aria-label="טוען דוח">
        <div className="h-9 w-72 animate-pulse rounded bg-gray-200 motion-reduce:animate-none" />
        <div className="mt-6 h-28 animate-pulse rounded-2xl bg-white motion-reduce:animate-none" />
        <div className="mt-6 h-96 animate-pulse rounded-2xl bg-white motion-reduce:animate-none" />
      </div>
    );
  }

  if (reportQuery.isError || !reportQuery.data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <FileSearch className="mx-auto h-10 w-10 text-gray-300" />
        <h1 className="mt-4 text-lg font-semibold text-gray-900">הדוח לא נמצא או שאינו זמין עבורך</h1>
        <Link to="/reports" className="mt-5 inline-flex min-h-11 items-center rounded-lg px-4 text-sm font-medium text-primary-700 hover:bg-primary-50">חזרה לחברות</Link>
      </div>
    );
  }

  const { report, runs } = reportQuery.data as CompanyReportDetail;
  const activeRun = runs.some((run) => run.status === 'pending' || run.status === 'running')
    || runStatus === 'pending'
    || runStatus === 'running';
  const canStart = report.document_status === 'processed' && !activeRun && !startRun.isPending;
  const percent = completionPercent(runStats);
  const total = corpusTotalCount(runStats) || values.length;
  const grounded = Number(runStats?.grounded || values.filter((value) => value.grounded === true).length);
  const currentRunForLabel = selectedRunMeta || (selectedRunId ? {
    run_id: selectedRunId,
    status: runStatus || 'pending',
    spec_version_id: '',
    spec_version_label: 'גרסה אחרונה',
    spec_profile: report.analysis_profile,
  } as CorpusRunSummary : null);

  return (
    <div className="mx-auto w-full max-w-[1500px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <nav className="mb-4 flex items-center gap-1 text-xs text-gray-500" aria-label="פירורי לחם">
        <Link to="/reports" className="flex min-h-11 items-center rounded-lg px-2 hover:bg-white hover:text-primary-700">חברות</Link>
        <ChevronLeft className="h-3.5 w-3.5" />
        <Link to={`/reports/companies/${report.company_id}`} className="flex min-h-11 max-w-52 items-center truncate rounded-lg px-2 hover:bg-white hover:text-primary-700">{report.company_name}</Link>
        <ChevronLeft className="h-3.5 w-3.5" />
        <span className="truncate text-gray-700">{reportPeriodLabel(report)}</span>
      </nav>

      <header className="flex flex-col gap-5 border-b border-gray-200 pb-6 xl:flex-row xl:items-end xl:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight text-gray-950 sm:text-3xl">{report.company_name}</h1>
            <DocumentStatusBadge status={report.document_status} progress={report.processing_progress} />
          </div>
          <p className="mt-2 text-sm text-gray-600">
            {reportPeriodLabel(report)} · {report.page_count ? `${report.page_count} עמודים · ` : ''}נוסף {formatReportDate(report.created_at)}
          </p>
          <p className="mt-1 text-xs font-medium text-primary-700">{analysisProfileLabel(report.analysis_profile)}</p>
          <p className="mt-1 max-w-2xl truncate text-xs text-gray-400" title={report.document_name}>{report.document_name}</p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {runs.length > 0 && (
            <label className="min-w-64">
              <span className="sr-only">בחירת ריצה</span>
              <select
                value={selectedRunId}
                onChange={(event) => setSelectedRunId(event.target.value)}
                className="h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/15"
              >
                {!selectedRunMeta && selectedRunId && <option value={selectedRunId}>ריצה חדשה · בתהליך</option>}
                {runs.map((run) => <option key={run.run_id} value={run.run_id}>{runLabel(run)}</option>)}
              </select>
            </label>
          )}
          <label className="min-w-40">
            <span className="sr-only">עומק הניתוח</span>
            <select
              value={runDepth}
              onChange={(event) => setRunDepth(event.target.value as 'easy' | 'all')}
              disabled={activeRun}
              className="h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/15 disabled:bg-gray-100"
            >
              <option value="easy">בדיקת ליבה</option>
              <option value="all">ניתוח מלא</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => startRun.mutate()}
            disabled={!canStart}
            title={report.document_status !== 'processed' ? 'החילוץ יתאפשר לאחר השלמת עיבוד המסמך' : activeRun ? 'קיימת ריצה פעילה' : undefined}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
          >
            {startRun.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : activeRun ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            {startRun.isPending ? 'מתחיל…' : activeRun ? 'החילוץ פועל' : runs.length ? 'ריצה חדשה' : 'הפעל חילוץ'}
          </button>
        </div>
      </header>

      {currentRunForLabel ? (
        <section className="mt-6 overflow-hidden rounded-2xl border border-gray-200 bg-white" aria-label="סיכום ריצה">
          <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <RunStatusBadge status={(runStatus || currentRunForLabel.status) as CorpusRunSummary['status']} />
              <div>
                <p className="text-sm font-semibold text-gray-900">ריצת חילוץ מקורית · {currentRunForLabel.spec_version_label}</p>
                <p className="mt-0.5 text-xs text-gray-500">
                  {analysisProfileLabel(currentRunForLabel.spec_profile)} · {currentRunForLabel.difficulty === 'easy' ? 'בדיקת ליבה' : !currentRunForLabel.difficulty ? 'ניתוח מלא' : currentRunForLabel.difficulty}
                </p>
              </div>
            </div>
            {values.length > 0 && (
              <button
                type="button"
                onClick={() => downloadCorpusCsv(values, `${report.company_name}-${report.period_year}-corpus.csv`)}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                <Download className="h-4 w-4" /> ייצוא CSV
              </button>
            )}
          </div>

          {(runStatus === 'pending' || runStatus === 'running') && (
            <div className="border-b border-primary-100 bg-primary-50/70 px-5 py-3" aria-live="polite">
              <div className="flex items-center gap-3 text-sm text-primary-900">
                <Loader2 className="h-4 w-4 animate-spin" />
                החילוץ רץ ברקע. המסך יתעדכן אוטומטית עם סיום האימות.
              </div>
            </div>
          )}

          {(runStatus === 'error' || currentRunForLabel.status === 'error') && (
            <div className="border-b border-error-200 bg-error-50 px-5 py-4 text-sm text-error-800" role="alert">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div><strong>הריצה נכשלה.</strong> {runQuery.data?.error || currentRunForLabel.error || 'אפשר לנסות ריצה חדשה.'}</div>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-4">
            {[
              { label: 'כיסוי ראיות', value: total ? `${percent}%` : '—', sub: total ? `${grounded} מתוך ${total} מאומתים או מחושבים` : 'אין נתונים', icon: ShieldCheck },
              { label: 'מאומתים מספרית', value: grounded || '0', sub: 'ערכים שעברו עיגון', icon: CheckCircle2 },
              { label: 'דורשים בדיקה', value: Number(runStats?.needs_review || statusCounts.needs_review), sub: 'ערכים שנמצאו ולא אומתו', icon: FileSearch },
              { label: 'פערים', value: Number(runStats?.flagged || statusCounts.flagged) + Number(runStats?.missing || statusCounts.missing), sub: 'חסר או חריג', icon: AlertTriangle },
            ].map((metric, index) => {
              const Icon = metric.icon;
              return (
                <div key={metric.label} className={`flex items-center gap-3 px-5 py-4 ${index ? 'border-t border-gray-100 sm:border-r sm:border-t-0' : ''}`}>
                  <Icon className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-xl font-semibold tabular-nums text-gray-950">{metric.value}</div>
                    <div className="text-xs font-medium text-gray-600">{metric.label}</div>
                    <div className="mt-0.5 text-[11px] text-gray-400">{metric.sub}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {total > 0 && (
            <div className="h-1.5 bg-gray-100" aria-label={`שלמות ריצה ${percent}%`}>
              <div className="h-full bg-primary-600 transition-[width] duration-300" style={{ width: `${percent}%` }} />
            </div>
          )}
        </section>
      ) : (
        <section className="mt-6 rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
          <FileSearch className="mx-auto h-10 w-10 text-gray-300" />
          <h2 className="mt-4 text-base font-semibold text-gray-800">עדיין לא בוצע חילוץ לדוח</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-gray-500">
            {report.document_status === 'processed'
              ? 'בחר בדיקת ליבה או ניתוח מלא והפעל חילוץ. יחסים חשבונאיים יחושבו רק מנתונים שעוגנו למקור.'
              : `המסמך עדיין בעיבוד. ${report.processing_stage || 'החילוץ יתאפשר כשהמסמך יהיה מוכן.'}`}
          </p>
        </section>
      )}

      {runStatus === 'done' && selectedRunId && <CompletionWorkbench runId={selectedRunId} />}

      {runStatus === 'done' && (
        <section className="mt-6" aria-labelledby="corpus-values-title">
          <div className="mb-3 flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="flex items-start gap-2.5">
              <History className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 id="corpus-values-title" className="text-sm font-semibold text-gray-900">תוצאת החילוץ המקורית</h2>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">תיעוד · לקריאה בלבד</span>
                </div>
                <p className="mt-1 max-w-2xl text-xs leading-5 text-gray-500">הטבלה מתעדת את הריצה המקורית ואינה משתנה לאחר השלמות. הסטטוס העדכני והראיות שאושרו מופיעים בתיק השלמת הנתונים למעלה.</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative sm:w-72">
                <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="חיפוש סעיף או ערך"
                  className="h-11 w-full rounded-xl border border-gray-300 bg-white pr-9 pl-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/15"
                />
              </div>
              <button
                type="button"
                onClick={() => valuesQuery.refetch()}
                disabled={valuesQuery.isFetching}
                className="flex h-11 w-11 items-center justify-center self-end rounded-xl border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                aria-label="רענן את תוצאת החילוץ המקורית"
                title="רענן את תוצאת החילוץ המקורית"
              >
                <RefreshCw className={`h-4 w-4 ${valuesQuery.isFetching ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          <div className="mb-3 flex gap-2 overflow-x-auto pb-1 scrollbar-hide" role="tablist" aria-label="קטגוריות">
            <button
              type="button"
              role="tab"
              aria-selected={selectedCategory === 'all'}
              onClick={() => setSelectedCategory('all')}
              className={`min-h-11 flex-shrink-0 rounded-xl border px-3 text-sm font-medium ${selectedCategory === 'all' ? 'border-primary-300 bg-primary-50 text-primary-800' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              הכל <span className="mr-1 text-xs tabular-nums opacity-70">{values.length}</span>
            </button>
            {categories.map(([category, count]) => (
              <button
                key={category}
                type="button"
                role="tab"
                aria-selected={selectedCategory === category}
                onClick={() => setSelectedCategory(category)}
                className={`min-h-11 flex-shrink-0 rounded-xl border px-3 text-sm font-medium ${selectedCategory === category ? 'border-primary-300 bg-primary-50 text-primary-800' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {category} <span className="mr-1 text-xs tabular-nums opacity-70">{count}</span>
              </button>
            ))}
          </div>

          <div className="mb-3 flex flex-wrap gap-2" aria-label="סינון לפי מצב">
            {([
              ['all', 'הכל'],
              ['filled', 'מאומת'],
              ['calculated', 'מחושב'],
              ['needs_review', 'לבדיקה'],
              ['missing', 'לא נמצא'],
              ['flagged', 'לא אומת'],
              ['not_applicable', 'לא רלוונטי'],
            ] as Array<[StatusFilter, string]>).map(([status, label]) => (
              <button
                key={status}
                type="button"
                aria-pressed={statusFilter === status}
                onClick={() => setStatusFilter(status)}
                className={`min-h-9 rounded-full border px-3 text-xs font-medium ${statusFilter === status ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {label} · {statusCounts[status]}
              </button>
            ))}
          </div>

          {valuesQuery.isLoading ? (
            <div className="h-80 animate-pulse rounded-2xl bg-white motion-reduce:animate-none" />
          ) : valuesQuery.isError ? (
            <div className="rounded-2xl border border-error-200 bg-error-50 px-5 py-8 text-center text-sm text-error-800">
              טעינת תוצאות הריצה נכשלה. <button onClick={() => valuesQuery.refetch()} className="mr-2 font-semibold underline">נסה שוב</button>
            </div>
          ) : filteredValues.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
              <Search className="mx-auto h-8 w-8 text-gray-300" />
              <p className="mt-3 text-sm text-gray-600">אין סעיפים שמתאימים לסינון הנוכחי.</p>
            </div>
          ) : (
            <>
              <div className="hidden overflow-hidden rounded-2xl border border-gray-200 bg-white lg:block">
                <table className="w-full table-fixed text-right text-sm">
                  <thead className="border-b border-gray-200 bg-gray-50 text-xs text-gray-600">
                    <tr>
                      <th className="w-24 px-4" aria-sort={sortKey === 'item_key' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                        <button type="button" onClick={() => toggleSort('item_key')} className="flex min-h-11 items-center gap-1 font-medium">מזהה {sortKey === 'item_key' && (sortDirection === 'asc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}</button>
                      </th>
                      <th className="w-[34%] px-4" aria-sort={sortKey === 'name' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                        <button type="button" onClick={() => toggleSort('name')} className="flex min-h-11 items-center gap-1 font-medium">סעיף {sortKey === 'name' && (sortDirection === 'asc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}</button>
                      </th>
                      <th className="w-[20%] px-4 font-medium">ערך</th>
                      <th className="w-32 px-4" aria-sort={sortKey === 'status' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                        <button type="button" onClick={() => toggleSort('status')} className="flex min-h-11 items-center gap-1 font-medium">מצב בחילוץ {sortKey === 'status' && (sortDirection === 'asc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}</button>
                      </th>
                      <th className="w-[18%] px-4 font-medium">מקור</th>
                      <th className="w-14 px-2"><span className="sr-only">פרטים</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredValues.map((value) => {
                      const isExpanded = expanded.has(value.item_key);
                      return (
                        <React.Fragment key={value.item_key}>
                          <tr className={isExpanded ? 'bg-primary-50/40' : 'hover:bg-gray-50'}>
                            <td className="px-4 py-3 font-mono text-xs text-gray-500" dir="ltr">{value.item_key}</td>
                            <td className="px-4 py-3">
                              <div className="line-clamp-2 font-medium text-gray-900">{value.name || value.item_key}</div>
                              <div className="mt-1 truncate text-xs text-gray-500">{value.category}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="line-clamp-2 font-semibold text-gray-900" dir="auto">{value.value || '—'}</div>
                              {value.unit && <div className="mt-1 text-xs text-gray-500">{value.unit}</div>}
                            </td>
                            <td className="px-4 py-3"><ValueStatusBadge status={value.status} /></td>
                            <td className="px-4 py-3 text-xs text-gray-600">
                              <div className="truncate">{value.section_title || 'לא צוין פרק'}</div>
                              <div className="mt-1 text-gray-400">{value.page ? `עמוד ${value.page}` : value.grounded === true ? 'עוגן נמצא' : 'ללא מספר עמוד'}</div>
                            </td>
                            <td className="px-2 py-3">
                              <button
                                type="button"
                                onClick={() => toggleExpanded(value.item_key)}
                                className="flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 hover:bg-white hover:text-primary-700"
                                aria-expanded={isExpanded}
                                aria-label={`${isExpanded ? 'סגור' : 'פתח'} פרטי ${value.item_key}`}
                              >
                                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-primary-50/40">
                              <td colSpan={6} className="px-6 pb-5 pt-1">
                                <ValueEvidence value={value} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="space-y-3 lg:hidden">
                {filteredValues.map((value) => {
                  const isExpanded = expanded.has(value.item_key);
                  return (
                    <article key={value.item_key} className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                      <button
                        type="button"
                        onClick={() => toggleExpanded(value.item_key)}
                        className="w-full px-4 py-4 text-right"
                        aria-expanded={isExpanded}
                      >
                        <div className="flex items-start gap-3">
                          <span className="mt-1 font-mono text-xs text-gray-400" dir="ltr">{value.item_key}</span>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-medium text-gray-900">{value.name || value.item_key}</h3>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <ValueStatusBadge status={value.status} />
                              {value.value && <span className="font-semibold text-gray-900" dir="auto">{value.value} {value.unit}</span>}
                            </div>
                          </div>
                          {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                        </div>
                      </button>
                      {isExpanded && <div className="border-t border-gray-100 px-4 py-4"><ValueEvidence value={value} /></div>}
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
};

const ValueEvidence: React.FC<{ value: CorpusValue }> = ({ value }) => {
  const formula = typeof value.extra?.formula === 'string' ? value.extra.formula : null;
  const extraInputs = value.extra?.inputs;
  const inputs = Array.isArray(extraInputs)
    ? extraInputs.filter((input): input is { item_key: string; value: string; unit?: string } => (
      typeof input === 'object'
      && input !== null
      && typeof (input as { item_key?: unknown }).item_key === 'string'
      && typeof (input as { value?: unknown }).value === 'string'
    ))
    : [];

  return (
    <div className="grid gap-4 text-sm xl:grid-cols-[minmax(0,1.5fr)_minmax(260px,1fr)]">
      <div>
        <div className="mb-1.5 text-xs font-semibold text-gray-600">{value.status === 'calculated' ? 'חישוב דטרמיניסטי' : 'ציטוט מהדוח'}</div>
        {value.status === 'calculated' && formula ? (
          <div className="rounded-xl border border-primary-200 bg-white px-4 py-3 text-gray-800">
            <div className="font-mono text-xs" dir="ltr">{formula}</div>
            {inputs.length > 0 && (
              <ul className="mt-3 space-y-1.5 border-t border-gray-100 pt-3 text-xs">
                {inputs.map((input) => (
                  <li key={input.item_key} className="flex items-center justify-between gap-3">
                    <span className="font-mono text-gray-500" dir="ltr">{input.item_key}</span>
                    <span className="font-medium text-gray-900" dir="auto">{input.value} {input.unit || ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : value.quote ? (
          <blockquote className="rounded-xl border border-gray-200 bg-white px-4 py-3 leading-7 text-gray-800" dir="auto">{value.quote}</blockquote>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-3 text-gray-500">לא נשמר ציטוט עבור סעיף זה.</div>
        )}
      </div>
      <dl className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-xs">
        <div className="grid grid-cols-[90px_1fr] gap-2 py-1.5">
          <dt className="font-medium text-gray-500">פרק</dt>
          <dd className="text-gray-800">{value.section_title || '—'}</dd>
        </div>
        <div className="grid grid-cols-[90px_1fr] gap-2 border-t border-gray-100 py-1.5">
          <dt className="font-medium text-gray-500">עמוד</dt>
          <dd className="text-gray-800">{value.page || '—'}</dd>
        </div>
        <div className="grid grid-cols-[90px_1fr] gap-2 border-t border-gray-100 py-1.5">
          <dt className="font-medium text-gray-500">עיגון</dt>
          <dd className="text-gray-800">{value.status === 'calculated' ? 'חושב מנתוני מקור מעוגנים' : value.grounded === true ? 'אומת מול טקסט המקור' : value.grounded === false ? 'לא אומת' : 'לא ניתן לבדיקה מספרית'}</dd>
        </div>
        <div className="grid grid-cols-[90px_1fr] gap-2 border-t border-gray-100 py-1.5">
          <dt className="font-medium text-gray-500">הנחיית מקור</dt>
          <dd className="text-gray-800">{value.source_hint || '—'}</dd>
        </div>
        {value.calc && (
          <div className="grid grid-cols-[90px_1fr] gap-2 border-t border-gray-100 py-1.5">
            <dt className="font-medium text-gray-500">חישוב</dt>
            <dd className="text-gray-800">{value.calc}</dd>
          </div>
        )}
      </dl>
    </div>
  );
};

export default ReportsReportPage;
